import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useViewMode } from '@/lib/view-mode-context';
import { useUserSettings } from '@/lib/user-settings-context';
import { startOfDay, endOfDay, isWithinInterval } from "date-fns";
import { UiStrategy } from "@/ui/UiStrategy";
import type { SessionsFilter } from "@/ui/view-props";
import { SessionsClassicView } from "@/ui/v1/SessionsClassicView";
import { SessionsModernView } from "@/ui/v2/SessionsModernView";
import { SessionsNeonView } from "@/ui/v3/SessionsNeonView";
import { useSessions, useUpdateSession } from '@/hooks/use-sessions';
import { usePlayers } from '@/hooks/use-players';

export default function Sessions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { targetUserId, isReadOnly } = useViewMode();
  const { settings } = useUserSettings();
  
  const [filter, setFilter] = useState<SessionsFilter>('all');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [uploadingSessionId, setUploadingSessionId] = useState<string | null>(null);
  const [scoreSheetUrls, setScoreSheetUrls] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');

  const { data: allSessions = [], isLoading: sessionsLoading } = useSessions(targetUserId, filter);
  const { data: players = [], isLoading: playersLoading } = usePlayers(targetUserId);
  const updateSessionMutation = useUpdateSession();

  const loading = sessionsLoading || playersLoading;

  const stats = useMemo(() => {
    return {
      totalSessions: allSessions.length,
      unsettled: allSessions.filter(s => s.status === 'unsettled').length,
      players: players.length
    };
  }, [allSessions, players]);

  const displayedSessions = useMemo(() => {
    let filtered = allSessions;

    if (settings.session_calendar_search && selectedDate) {
      const start = startOfDay(selectedDate);
      const end = endOfDay(selectedDate);
      filtered = filtered.filter(s => {
        const d = new Date(s.date);
        return isWithinInterval(d, { start, end });
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.session_players?.some((sp: any) =>
          sp.players?.name?.toLowerCase().includes(query)
        ) || s.date?.includes(searchQuery)
      );
    }

    return filtered;
  }, [allSessions, settings.session_calendar_search, selectedDate, searchQuery]);

  useEffect(() => {
    const sessionsWithImages = displayedSessions.filter(s => s.score_sheet_path && !scoreSheetUrls[s.id]);
    if (sessionsWithImages.length > 0) {
      const paths = sessionsWithImages.map(s => s.score_sheet_path);
      const fetchUrls = async () => {
        const { data: signedData, error } = await supabase.storage
          .from('scoresheets')
          .createSignedUrls(paths as string[], 3600);
        
        if (error) {
          console.error("Error fetching signed URLs:", error);
        } else if (signedData) {
          const urlMap: Record<string, string> = {};
          signedData.forEach((item, index) => {
            if (item.signedUrl) {
              const sessionId = sessionsWithImages[index].id;
              urlMap[sessionId] = item.signedUrl;
            }
          });
          setScoreSheetUrls(prev => ({ ...prev, ...urlMap }));
        }
      };
      fetchUrls();
    }
  }, [displayedSessions, scoreSheetUrls]);

  const handlePlayerSelect = (playerId: string) => {
    navigate(isReadOnly ? `/public/${targetUserId}/player/${playerId}` : `/player/${playerId}`);
  };

  const handleUploadScoreSheet = async (sessionId: string, file: File) => {
    if (isReadOnly || !user) return;
    
    try {
      setUploadingSessionId(sessionId);
      
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/${sessionId}/score.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('scoresheets')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      await updateSessionMutation.mutateAsync({
        id: sessionId,
        score_sheet_path: filePath
      });
      
      const { data: signedData } = await supabase.storage
          .from('scoresheets')
          .createSignedUrl(filePath, 3600);
      
      if (signedData?.signedUrl) {
        setScoreSheetUrls(prev => ({ ...prev, [sessionId]: signedData.signedUrl }));
      }
    } catch (error: any) {
      console.error('Error uploading score sheet:', error.message);
      alert('Error uploading score sheet: ' + error.message);
    } finally {
      setUploadingSessionId(null);
    }
  };

  const props = {
    sessions: displayedSessions,
    loading: loading && allSessions.length === 0,
    filter,
    setFilter,
    selectedDate,
    setSelectedDate,
    settings,
    isReadOnly,
    targetUserId,
    stats,
    onPlayerSelect: handlePlayerSelect,
    onUploadScoreSheet: handleUploadScoreSheet,
    isUploading: uploadingSessionId,
    scoreSheetUrls,
    searchQuery,
    setSearchQuery,
  };

  return (
    <UiStrategy
      classic={<SessionsClassicView {...props} />}
      modern={<SessionsModernView {...props} />}
      neon={<SessionsNeonView {...props} />}
    />
  );
}
